import { Banner, Button, DatePicker, Field, FieldGrid, Input, Modal, Switch } from '../../../../../ui'
import s from './styles.module.scss'

export interface CreditReportForm {
    firstname: string
    lastname: string
    email: string
    telephone: string
    dob: Date | null
    flat: string
    house_name: string
    house_number: string
    street: string
    town_city: string
    county: string
    postcode: string
}

interface CreditReportModalProps {
    open: boolean
    form: CreditReportForm
    editing: boolean
    submitting: boolean
    hasExistingReport: boolean
    onClose: () => void
    onToggleEditing: (editing: boolean) => void
    onChange: (patch: Partial<CreditReportForm>) => void
    onSubmit: () => void
}

type TextKey = Exclude<keyof CreditReportForm, 'dob'>

/** Review (and optionally correct) the client's details before a credit check is submitted. */
export const CreditReportModal = ({
    open,
    form,
    editing,
    submitting,
    hasExistingReport,
    onClose,
    onToggleEditing,
    onChange,
    onSubmit,
}: CreditReportModalProps) => {
    const text = (key: TextKey, label: string, type: string = 'text', span?: 1 | 2 | 'all') => (
        <Field label={label} span={span} key={key}>
            {(id) => (
                <Input
                    id={id}
                    type={type}
                    value={form[key]}
                    disabled={!editing}
                    onChange={(e) => onChange({ [key]: e.target.value } as Partial<CreditReportForm>)}
                />
            )}
        </Field>
    )

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="lg"
            title={hasExistingReport ? 'Re-run credit report' : 'Run credit report'}
            subtitle="Confirm the details that will be submitted to the credit reference agency"
            busy={submitting}
            closeOnBackdrop={!submitting}
            footer={
                <>
                    <Button onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" loading={submitting} onClick={onSubmit}>
                        {hasExistingReport ? 'Re-run credit report' : 'Run credit report'}
                    </Button>
                </>
            }
        >
            <div className={s.stack}>
                <Banner tone="info">
                    Details are pre-filled from the client record. Turn on editing to correct anything before the check
                    is submitted; the client record itself is not changed.
                </Banner>

                <Switch checked={editing} onChange={(e) => onToggleEditing(e.target.checked)} label="Enable editing" />

                <FieldGrid minColumn={200}>
                    {text('firstname', 'First name')}
                    {text('lastname', 'Last name')}
                    {text('email', 'Email', 'email')}
                    {text('telephone', 'Telephone', 'tel')}
                    <Field label="Date of birth">
                        <DatePicker
                            selected={form.dob}
                            onChange={(d) => onChange({ dob: (d as Date | null) ?? null })}
                            disabled={!editing}
                            maxDate={new Date()}
                        />
                    </Field>
                    {text('flat', 'Flat')}
                    {text('house_name', 'House name')}
                    {text('house_number', 'House number')}
                    {text('street', 'Street', 'text', 2)}
                    {text('town_city', 'Town / city')}
                    {text('county', 'County')}
                    {text('postcode', 'Postcode')}
                </FieldGrid>
            </div>
        </Modal>
    )
}
